import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, Building2, ClipboardList } from "lucide-react";
import { Reveal, Overline, MaskedHeading } from "@/components/ui-bits";
import { stagger, fadeUp, viewport } from "@/lib/motion";
import { BOOKING_URL } from "@/lib/api";

const IMG =
  "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc4NTU3MjA1Mnww&ixlib=rb-4.1.0&q=85";

const ITEMS = [
  {
    icon: BadgeCheck,
    title: "Citizenship & Residency",
    text: "Guidance through the citizenship application and residency permit processes with clarity, not confusion.",
  },
  {
    icon: ClipboardList,
    title: "Relocation Project Management",
    text: "From verifying your land to overseeing construction, we manage your relocation project while you're still abroad.",
  },
  {
    icon: Building2,
    title: "Business Setup for Newcomers",
    text: "Registering a business and navigating local requirements without the paperwork traps that catch new arrivals off guard.",
  },
];

export default function Settlement() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="settlement"
      ref={ref}
      className="relative bg-navy-deep grain py-24 sm:py-32 overflow-hidden"
      data-testid="settlement-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="relative overflow-hidden border border-gold/25">
              <motion.img
                style={{ y: imgY, scale: 1.12 }}
                src={IMG}
                alt="Modern property under construction in Ghana"
                className="w-full h-[420px] sm:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display italic text-white text-lg">
                  “Come to Ghana with a plan.”
                </p>
              </div>
            </div>
            <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-gold" />
            <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-2 border-r-2 border-gold" />
          </Reveal>

          <div className="order-1 lg:order-2">
            <Reveal>
              <Overline index={4}>Ghana Settlement Services</Overline>
            </Reveal>
            <MaskedHeading
              tag="h2"
              className="mt-6 font-display font-bold text-white leading-[1.06] tracking-tight text-3xl sm:text-4xl lg:text-5xl"
              lines={["Are You Considering", "Moving to Ghana?"]}
            />
            <Reveal delay={0.1}>
              <div className="mt-7 space-y-5 text-white/75 leading-relaxed max-w-xl">
                <p>
                  Making Ghana your home is exciting — but figuring out where to
                  even start can quickly become overwhelming. BAT Ear Research is
                  here to help.
                </p>
                <p>
                  Whether you need support with citizenship, business
                  registration, legal documentation, land and property due
                  diligence, or simply understanding how to get properly
                  established — we walk with you through every step.
                </p>
                <p className="text-white">
                  You don't have to arrive and figure it all out on your own.
                  Come to Ghana with a plan, and someone on the ground who knows
                  how to help you navigate it.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="mt-9">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="settlement-cta"
                className="group inline-flex items-center gap-3 border border-gold text-gold hover:bg-gold hover:text-navy-deep transition-colors duration-300 font-semibold px-7 py-4 text-sm uppercase tracking-wider"
              >
                Schedule Your Settlement Strategy Session
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </a>
            </Reveal>
          </div>
        </div>

        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-16 grid md:grid-cols-3 gap-5"
        >
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                variants={fadeUp}
                className="group border border-white/10 hover:border-gold/50 p-8 transition-colors duration-500"
                data-testid={`settlement-card-${i + 1}`}
              >
                <Icon size={26} className="text-gold" />
                <h3 className="mt-5 font-display text-xl font-semibold text-white">
                  {it.title}
                </h3>
                <p className="mt-3 text-white/60 leading-relaxed text-[15px]">
                  {it.text}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        <Reveal delay={0.1} className="mt-8">
          <p className="text-xs text-white/40 leading-relaxed max-w-3xl">
            BAT Ear Research coordinates and facilitates — we do not provide
            legal advice, notarial services, or represent any government body.
            All statutory and legal work is completed by the appropriate
            licensed professionals and authorities.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
